/* eslint-disable react/prop-types */
import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@helpers/styleUtils';

const Select = React.forwardRef(
    ({ className, value, onChange, options, placeholder = 'Select', ...props }, ref) => {
        // Ensure options always has a valid value
        const sanitizedOptions = React.useMemo(() => {
            return options
                .map(option => ({
                    ...option,
                    value: option.value ?? option.label, // Fallback to label if value is null/undefined
                    label: option.label ?? option.value, // Fallback to value if label is null/undefined
                }))
                .filter(option => option.value !== ''); // Filter out empty values
        }, [options]);

        return (
            <SelectPrimitive.Root value={value || undefined} onValueChange={onChange} {...props}>
                <SelectPrimitive.Trigger
                    ref={ref}
                    className={cn(
                        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                        className,
                    )}
                >
                    <SelectPrimitive.Value placeholder={placeholder} />
                    <SelectPrimitive.Icon>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                    </SelectPrimitive.Icon>
                </SelectPrimitive.Trigger>
                <SelectPrimitive.Portal>
                    <SelectPrimitive.Content
                        className="relative z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80"
                        position="popper"
                        sideOffset={5}
                    >
                        <SelectPrimitive.Viewport className="p-1">
                            {sanitizedOptions.map(option => (
                                <SelectPrimitive.Item
                                    key={option.value}
                                    value={option.value}
                                    className={cn(
                                        'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                                        'hover:bg-accent hover:text-accent-foreground',
                                    )}
                                >
                                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                        <SelectPrimitive.ItemIndicator>
                                            <Check className="h-4 w-4" />
                                        </SelectPrimitive.ItemIndicator>
                                    </span>
                                    <SelectPrimitive.ItemText>
                                        {option.label}
                                    </SelectPrimitive.ItemText>
                                </SelectPrimitive.Item>
                            ))}
                        </SelectPrimitive.Viewport>
                    </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
        );
    },
);

Select.displayName = 'Select';

export { Select };
